import { useEffect, useState, useCallback } from 'react'
import { useNavigate } from 'react-router-dom'
import { supabase } from '../lib/supabase'
import { useCare } from '../contexts/CareContext'
import { useToast, humanizeError } from '../components/shared/Toast'
import LoadingSpinner from '../components/shared/LoadingSpinner'
import EmptyState from '../components/shared/EmptyState'
import DocumentForm, { CATEGORIES } from '../components/documents/DocumentForm'
import DocumentViewer from '../components/documents/DocumentViewer'

const IMAGE_RE = /\.(png|jpe?g|gif|webp|heic|heif|bmp|svg)$/i
const kindOf = (fileName = '') =>
  IMAGE_RE.test(fileName) ? 'image' : /\.pdf$/i.test(fileName) ? 'pdf' : 'other'

const LABEL = Object.fromEntries(CATEGORIES.map((c) => [c.value, c.label]))
const fmtDate = (iso) => new Date(iso + 'T00:00:00').toLocaleDateString(undefined, { month: 'short', day: 'numeric', year: 'numeric' })
const fmtSize = (b) => (b == null ? '' : b < 1024 * 1024 ? `${Math.round(b / 1024)} KB` : `${(b / 1024 / 1024).toFixed(1)} MB`)

export default function DocumentsPage() {
  const { recipient, role } = useCare()
  const toast = useToast()
  const navigate = useNavigate()
  const canDelete = role === 'admin' || role === 'family_member'

  const [docs, setDocs] = useState([])
  const [loading, setLoading] = useState(true)
  const [showForm, setShowForm] = useState(false)
  const [filter, setFilter] = useState('all')
  const [opening, setOpening] = useState(null)
  const [viewing, setViewing] = useState(null) // { url, name, kind }

  const load = useCallback(async () => {
    if (!recipient) return
    const { data } = await supabase
      .from('documents')
      .select('*, uploader:profiles(full_name)')
      .eq('care_recipient_id', recipient.id)
      .order('document_date', { ascending: false, nullsFirst: false })
      .order('created_at', { ascending: false })
    setDocs(data || [])
    setLoading(false)
  }, [recipient])

  useEffect(() => { load() }, [load])

  async function open(doc) {
    setOpening(doc.id)
    try {
      const { data, error } = await supabase.storage.from('documents').createSignedUrl(doc.file_url, 3600, { download: false })
      if (error) throw error
      setViewing({ url: data.signedUrl, name: doc.file_name, kind: kindOf(doc.file_name) })
    } catch (err) {
      toast.error(humanizeError(err))
    } finally {
      setOpening(null)
    }
  }

  async function remove(doc) {
    if (!window.confirm(`Delete "${doc.file_name}"? This cannot be undone.`)) return
    try {
      const { error } = await supabase.from('documents').delete().eq('id', doc.id)
      if (error) throw error
      await supabase.storage.from('documents').remove([doc.file_url]) // best-effort
      setDocs((d) => d.filter((x) => x.id !== doc.id))
      toast.success('Document deleted.')
    } catch (err) {
      toast.error(humanizeError(err))
    }
  }

  if (!recipient || loading) return <LoadingSpinner label="Loading documents…" />

  const cats = ['all', ...new Set(docs.map((d) => d.category))]
  const shown = filter === 'all' ? docs : docs.filter((d) => d.category === filter)

  return (
    <div className="stack">
      <button onClick={() => navigate('/more')} style={{ background: 'none', border: 'none', color: 'var(--teal)', fontWeight: 600, alignSelf: 'flex-start', padding: 0 }}>
        ← More</button>

      <div className="spread">
        <h1 className="title">Documents</h1>
        <button className="btn btn-sm" onClick={() => setShowForm(true)}>+ Upload</button>
      </div>

      {docs.length > 0 && (
        <div className="row wrap" style={{ gap: 8 }}>
          {cats.map((c) => (
            <button key={c} className={`chip ${filter === c ? 'chip-on' : ''}`} onClick={() => setFilter(c)}>{c === 'all' ? 'All' : LABEL[c] || c}</button>
          ))}
        </div>
      )}

      {shown.length === 0 ? (
        <EmptyState icon="📄" title={docs.length === 0 ? 'No documents yet' : 'None in this category'} message={docs.length === 0 ? 'Upload lab results, letters, discharge summaries, and more.' : 'Try another category.'} action={docs.length === 0 && <button className="btn" onClick={() => setShowForm(true)}>+ Upload document</button>} />
      ) : (
        <div className="stack" style={{ gap: 10 }}>
          {shown.map((d) => (
            <div key={d.id} className="card" style={{ display: 'flex', gap: 12, alignItems: 'flex-start' }}>
              <div style={{ fontSize: 26, flexShrink: 0 }}>{d.file_name?.toLowerCase().endsWith('.pdf') ? '📄' : '🖼️'}</div>
              <button onClick={() => open(d)} className="grow" style={{ background: 'none', border: 'none', textAlign: 'left', padding: 0 }}>
                <div style={{ fontWeight: 600 }}>{d.file_name}{opening === d.id && <span className="muted"> · opening…</span>}</div>
                {d.description && <div className="muted" style={{ fontSize: 13 }}>{d.description}</div>}
                <div className="row wrap" style={{ gap: 6, marginTop: 6 }}>
                  <span className="badge">{LABEL[d.category] || d.category}</span>
                  {d.document_date && <span className="muted" style={{ fontSize: 12 }}>{fmtDate(d.document_date)}</span>}
                  {d.file_size_bytes != null && <span className="muted" style={{ fontSize: 12 }}>{fmtSize(d.file_size_bytes)}</span>}
                </div>
              </button>
              {canDelete && (
                <button onClick={() => remove(d)} style={{ background: 'none', border: 'none', color: 'var(--danger)', fontSize: 13, flexShrink: 0 }}>Delete</button>
              )}
            </div>
          ))}
        </div>
      )}

      {showForm && (
        <DocumentForm recipientId={recipient.id} onCancel={() => setShowForm(false)} onDone={() => { setShowForm(false); load() }} />
      )}

      {viewing && (
        <DocumentViewer
          url={viewing.url}
          name={viewing.name}
          kind={viewing.kind}
          onClose={() => setViewing(null)}
        />
      )}

      <div style={{ height: 8 }} />
    </div>
  )
}
